import {
	blankNameBlocksSave,
	newOverLongPersonSearchTitles,
	PERSON_SEARCH_TITLE_MAX_LENGTH,
	personSearchTitlePreview,
} from "@signalist/shared-types";
import { TFunction } from "i18next";

import { ScoreResponse } from "../schema/score.schema";

import { FOCUS_PARAM } from "./focusTarget";
import { scoreStepPath } from "./scoreSteps";

/**
 * Why the draft cannot be saved, and which row to show (PREC-4987): a segment or persona the draft
 * created or renamed to a blank name, or a job title it added to a persona that is over the
 * people-search limit. What the server already holds passes untouched — a legacy blank segment
 * (segment 1367, a partner segment saved without a name) no longer blocks every save of its
 * workspace. The API refuses the same rows (`unsavableScoreReason`).
 */
export type SaveBlocker =
	| { kind: "blankName"; group: "PROSPECT" | "PARTNER" | "PERSONA"; position: number; id: number }
	| { kind: "titleTooLong"; personaId: number; personaName: string; title: string };

type Row = { id?: number; category: string };

const GROUPS = [
	["prospectCompanyCategories", "PROSPECT"],
	["partnerCompanyCategories", "PARTNER"],
	["prospectPersonaCategories", "PERSONA"],
] as const;

const storedById = <T extends Row>(rows: T[] | undefined): Map<number, T> =>
	new Map((rows ?? []).flatMap((row) => (row.id === undefined ? [] : [[row.id, row] as const])));

const blankNameBlocker = (draft: ScoreResponse, server: ScoreResponse): SaveBlocker | null => {
	for (const [list, group] of GROUPS) {
		const stored = storedById<Row>(server[list]);
		const rows: Row[] = draft[list];

		for (const [index, row] of rows.entries()) {
			const storedName = row.id === undefined ? undefined : stored.get(row.id)?.category;

			if (blankNameBlocksSave(storedName, row.category)) {
				return { kind: "blankName", group, position: index + 1, id: row.id ?? 0 };
			}
		}
	}

	return null;
};

const titleBlocker = (draft: ScoreResponse, server: ScoreResponse): SaveBlocker | null => {
	const stored = storedById(server.prospectPersonaCategories);

	for (const persona of draft.prospectPersonaCategories) {
		const storedTitles =
			persona.id === undefined ? undefined : stored.get(persona.id)?.jobsToInclude;
		const [first] = newOverLongPersonSearchTitles(storedTitles, persona.jobsToInclude);

		if (first && persona.id !== undefined) {
			return {
				kind: "titleTooLong",
				personaId: persona.id,
				personaName: persona.category,
				title: first.title,
			};
		}
	}

	return null;
};

/** The first row that blocks saving this draft against the server's copy, or null. */
export const firstSaveBlocker = (draft: ScoreResponse, server: ScoreResponse): SaveBlocker | null =>
	blankNameBlocker(draft, server) ?? titleBlocker(draft, server);

/** The message the save bar shows for a blocked save: the row's type and position, or the persona and title. */
export const saveBlockerText = (blocker: SaveBlocker, t: TFunction): string =>
	blocker.kind === "blankName"
		? t("leadScoring.shell.blankRowError", {
				row: t(`leadScoring.shell.blankRowKind.${blocker.group}`),
				position: blocker.position,
			})
		: t("leadScoring.shell.titleTooLongError", {
				persona: blocker.personaName,
				title: personSearchTitlePreview(blocker.title),
				limit: PERSON_SEARCH_TITLE_MAX_LENGTH,
			});

/** Where the blocked row is shown: its step, with the `?focus=` that selects and flashes it. */
export const saveBlockerHref = (blocker: SaveBlocker): string => {
	const [step, focus] =
		blocker.kind === "titleTooLong"
			? (["prospect", `persona:${blocker.personaId}`] as const)
			: blocker.group === "PERSONA"
				? (["prospect", `persona:${blocker.id}`] as const)
				: (["company", `segment:${blocker.id}`] as const);

	return `${scoreStepPath(step)}?${FOCUS_PARAM}=${encodeURIComponent(focus)}`;
};
